import { DatePipe } from '@angular/common';
import { ObjectPropertyFormat } from '@newroom-connect/library/enums';
import { IObjectProperty } from '@newroom-connect/library/interfaces';

import { FileHelper } from './file.helper';

export class ObjectHelper {
  private static readonly datePipe = new DatePipe('en-US');

  /**
   * Extract a property value from a given object by the given property key. It is possible to use the dot notation to extract a nested property of an object.
   *
   * @param objectToExtractFrom The object to extract the property value from by the given `propertyKey`.
   * @param propertyKey The key of the property to extract the value from.
   *
   * @returns The value of the object property.
   */
  public static extractProperty(objectToExtractFrom: unknown, propertyKey: string): unknown | Record<string, unknown> {
    const propertyNameParts = propertyKey.split('.');

    let objectPart: unknown = objectToExtractFrom;

    for (const propertyNamePart of propertyNameParts) {
      if (!objectPart || typeof objectPart !== 'object') {
        return undefined;
      }

      const propertyNamePartAsNumber = +propertyNamePart;

      if (!isNaN(propertyNamePartAsNumber) && Array.isArray(objectPart) && propertyNamePartAsNumber < objectPart.length) {
        objectPart = objectPart[propertyNamePartAsNumber];
      } else if (isNaN(propertyNamePartAsNumber) && Object.keys(objectPart).includes(propertyNamePart)) {
        objectPart = (objectPart as Record<string, unknown>)[propertyNamePart];
      } else {
        return undefined;
      }
    }

    return objectPart;
  }

  /**
   * Extract a list of properties list from a given object by the given property keys. It is possible to use the dot notation to extract a nested property of an object.
   * It is also possible to apply a format to the extracted property value. Currently, the following formats are supported:
   *
   * - TEXT: No format is applied to the extracted property value.
   * - DATE: The extracted property value is formatted as a date string 'yyyy-MM-dd hh:mm'.
   * - FILE_SIZE: The extracted property value is formatted as a file size string.
   *
   * It is also possible to add a prefix to the extracted property value, the prefix will be added to each of the extracted property values if the value is not empty.
   *
   * @param objectToExtractFrom The target object to extract the properties from.
   * @param propertiesToExtract The name of the properties to extract.
   *
   * @returns A list of extracted properties as a string separated by the given `delimiter`. Default delimiter is ','.
   */
  public static extractProperties<T = unknown>(objectToExtractFrom: unknown, propertiesToExtract: IObjectProperty[]): (T | string | unknown)[] {
    const result: (T | string | unknown)[] = [];

    propertiesToExtract.forEach((propertyToExtract: IObjectProperty) => {
      const value = ObjectHelper.extractProperty(objectToExtractFrom, propertyToExtract.key);

      if (value === undefined || value === null) {
        return;
      }

      let formattedValue: unknown = value;

      // Apply format if specified
      if (propertyToExtract.format) {
        switch (propertyToExtract.format) {
          case ObjectPropertyFormat.DATE: {
            const date = this.datePipe.transform(String(value), 'yyyy-MM-dd');

            if (typeof date === 'string') {
              formattedValue = date;
            }
            break;
          }
          case ObjectPropertyFormat.DATETIME: {
            const date = this.datePipe.transform(String(value), 'yyyy-MM-dd hh:mm');

            if (typeof date === 'string') {
              formattedValue = date;
            }
            break;
          }
          case ObjectPropertyFormat.FILE_SIZE: {
            const size = FileHelper.toFileSizeString(Number(value));

            if (typeof size === 'string') {
              formattedValue = size;
            }
            break;
          }
          default:
            // For TEXT or unknown, leave formattedValue as value
            break;
        }
      }

      // Add prefix if present and value is not empty
      if (propertyToExtract.prefix) {
        // Only show prefix if formattedValue string is not empty
        const stringVal = formattedValue !== undefined && formattedValue !== null ? String(formattedValue).trim() : '';

        if (stringVal.length > 0) {
          result.push(`${propertyToExtract.prefix} ${stringVal}`);
        }
      } else {
        result.push(formattedValue);
      }
    });

    return result;
  }

  /**
   * Performs a deep comparison between two objects to determine if they are equivalent.
   * Handles primitive types, arrays, objects, dates, and null/undefined values.
   *
   * @param obj1 The first object to compare.
   * @param obj2 The second object to compare.
   *
   * @returns True if the objects are deeply equal, false otherwise.
   */
  public static deepEquals(obj1: unknown, obj2: unknown): boolean {
    // Handle strict equality (same reference or primitive values)
    if (obj1 === obj2) {
      return true;
    }

    // Handle null/undefined cases
    if (obj1 === null || obj2 === null) {
      return obj1 === obj2;
    }

    // Handle different types
    if (typeof obj1 !== typeof obj2) {
      return false;
    }

    // Handle Date objects
    if (obj1 instanceof Date && obj2 instanceof Date) {
      return obj1.getTime() === obj2.getTime();
    }

    // Handle arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) {
        return false;
      }

      for (let i = 0; i < obj1.length; i++) {
        if (!ObjectHelper.deepEquals(obj1[i], obj2[i])) {
          return false;
        }
      }

      return true;
    }

    // Handle objects
    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
      const keys1 = Object.keys(obj1 as Record<string, unknown>);
      const keys2 = Object.keys(obj2 as Record<string, unknown>);

      if (keys1.length !== keys2.length) {
        return false;
      }

      for (const key of keys1) {
        if (!keys2.includes(key)) {
          return false;
        }

        const value1 = (obj1 as Record<string, unknown>)[key];
        const value2 = (obj2 as Record<string, unknown>)[key];

        if (!ObjectHelper.deepEquals(value1, value2)) {
          return false;
        }
      }

      return true;
    }

    // For all other cases (primitives that aren't strictly equal)
    return false;
  }

  /**
   * Check if a value is a defined valid object.
   *
   * @param value The value to check.
   *
   * @returns True if the value is a defined valid object, false otherwise.
   */
  public static isValidObject(value: unknown): boolean {
    return typeof value === 'object' && value !== null && value !== undefined;
  }

  /**
   * Parse a boolean value from persisted string storage.
   *
   * @param value The persisted value.
   *
   * @returns Parsed boolean value.
   */
  public static parseBoolean(value?: string | null): boolean {
    if (!value) {
      return false;
    }

    return value.toLowerCase() === 'true';
  }
}
